/**
 * What to call the signed-in person. Auth0 fills `name` with the EMAIL when the identity provider
 * gave no name — Sign in with Apple does exactly that (the first tester's dashboard read
 * "Hi jane.doe@privaterelay.appleid.com") — so an email-shaped name is never shown as a name.
 */
export type NamedUser = {
  given_name?: string;
  name?: string;
  nickname?: string;
  email?: string;
};

const looksLikeEmail = (s: string | undefined): boolean => !!s && s.includes("@");

const clean = (s: string | undefined): string | null => {
  const t = s?.trim();
  return t && !looksLikeEmail(t) ? t : null;
};

/** The person's full name, or null when nothing human-looking is known (callers choose the fallback). */
export function displayNameFor(user: NamedUser | undefined): string | null {
  if (!user) return null;
  return clean(user.name) ?? clean(user.given_name) ?? clean(user.nickname) ?? null;
}

/** The person's first name, or null. Apple relay nicknames are random letters, never a name. */
export function firstNameFor(user: NamedUser | undefined): string | null {
  if (!user) return null;
  const given = clean(user.given_name);
  if (given) return given.split(/\s+/)[0];
  const name = clean(user.name);
  if (name) return name.split(/\s+/)[0];
  const nick = clean(user.nickname);
  if (nick && !isAppleRelay(user.email)) return nick.split(/\s+/)[0];
  return null;
}

/** Auth0's nickname for an Apple relay address is the relay's random local part — not a name. */
function isAppleRelay(email: string | undefined): boolean {
  return !!email && email.toLowerCase().endsWith("@privaterelay.appleid.com");
}
